import React, { Component } from 'react';
import { Platform } from 'src/environment';
import { TYPES } from 'src/shared/di/types';
import { lazyInject, container } from 'src/shared/di';
import { MonitorService } from 'src/shared/services/monitoring';
import { RouteComponentProps } from 'src/shared/router';
import { ToggleContext, withToggleContext } from 'src/shared/toggles';
import { PageLoader } from '../PageLoader/PageLoader';

// props for async loading of a route
export interface AsyncRouteProps {
  module: () => Promise<unknown>;
  /**
   * Exported name of the module
   */
  export?: string;
}

// global props used for all routes
export interface RouteProps {
  // props leveraged to determine state for all routes
  isAnonymous: boolean;
  credentialsReceived: boolean;
  platform: Platform;
  setCredentials: (credentials: any) => void;
  isAuthenticated: boolean;
}
interface AsyncRouteState {
  Route: () => JSX.Element;
}

const DEFAULT_EXPORT = 'default';

export type AsyncRouteComponentProps = AsyncRouteProps &
  RouteProps &
  ToggleContext &
  RouteComponentProps & { key: string };

/**
 * Component for async loading of routes.
 * This will allow for the implementation and code splitting based on need rather than loading the entire app up front.
 */
export class AsyncRouteComponent extends Component<
  AsyncRouteComponentProps,
  AsyncRouteState
> {
  @lazyInject(TYPES.MonitorService)
  private readonly _monitorService: MonitorService;

  private get monitorService(): MonitorService {
    return (
      this._monitorService ||
      container.get<MonitorService>(TYPES.MonitorService)
    );
  }

  public state: AsyncRouteState = {
    Route: null,
  };

  public componentDidMount() {
    this.props
      .module()
      .then((module) => {
        this.setState({ Route: module[this.props.export || DEFAULT_EXPORT] });
      })
      .catch(console.error);
  }

  public render(): JSX.Element {
    const { contentfulTogglesInitialized } = this.props;
    const { Route } = this.state;
    const isPageLoading = !Route || !contentfulTogglesInitialized;

    if (isPageLoading) {
      return <PageLoader />;
    }

    return <Route {...this.props} />;
  }
}

export const AsyncRoute = withToggleContext<AsyncRouteComponentProps>(
  AsyncRouteComponent,
  'AsyncRoute',
);
